import { TLAssetId, TLShapeId } from "@tldraw/tldraw";
import FrontGuest from "static/media/guest-placeholders/FrontGuest.png";
import BackGuest from "static/media/guest-placeholders/BackGuest.png";

const MARKER_PREFIX = "guestPlaceholdersInjected_";

const formatShape = (editor: any, id: "shape:front" | "shape:back") => {
  try {
    return editor.getShape(id as any);
  } catch {
    return undefined;
  }
};

export type GuestPlaceholdersResult = "injected" | "already-injected" | "shapes-not-ready";

/**
 * On a guest's first entry to a fresh room we inject two marketing
 * placeholders — one over `shape:front`, one over `shape:back` — so the
 * canvas is never empty. Skipped once injected (localStorage marker keyed
 * by roomId), so refreshing or deleting the images does not bring them
 * back; the guest's edits stick across reloads via Liveblocks.
 *
 * Caller should poll while the result is `shapes-not-ready` (the format
 * shapes are created asynchronously by useMultiplayerState on first
 * mount) and stop on `injected` or `already-injected`.
 */
export const injectGuestPlaceholders = (editor: any, roomId: string): GuestPlaceholdersResult => {
  if (!editor || !roomId) return "shapes-not-ready";

  const markerKey = `${MARKER_PREFIX}${roomId}`;
  try {
    if (window.localStorage.getItem(markerKey) === "true") return "already-injected";
  } catch {
    /* localStorage unavailable — proceed; we'll just inject every load */
  }

  const front = formatShape(editor, "shape:front");
  const back = formatShape(editor, "shape:back");
  if (!front || !back) return "shapes-not-ready";

  const frontW = front.props?.w;
  const frontH = front.props?.h;
  const backW = back.props?.w;
  const backH = back.props?.h;
  if (!frontW || !frontH || !backW || !backH) return "shapes-not-ready";

  const frontAssetId = `asset:guest-front-${Date.now()}` as TLAssetId;
  const backAssetId = `asset:guest-back-${Date.now() + 1}` as TLAssetId;

  editor.createAssets([
    {
      id: frontAssetId,
      type: "image",
      typeName: "asset",
      props: {
        name: "FrontGuest.png",
        src: FrontGuest,
        w: frontW,
        h: frontH,
        mimeType: "image/png",
        isAnimated: false,
      },
      meta: { name: "FrontGuest.png" },
    },
    {
      id: backAssetId,
      type: "image",
      typeName: "asset",
      props: {
        name: "BackGuest.png",
        src: BackGuest,
        w: backW,
        h: backH,
        mimeType: "image/png",
        isAnimated: false,
      },
      meta: { name: "BackGuest.png" },
    },
  ]);

  editor.createShapes([
    {
      id: `shape:guest-front-image-${Date.now()}` as TLShapeId,
      type: "customImage",
      props: { assetId: frontAssetId, w: frontW, h: frontH },
      x: front.x,
      y: front.y,
      meta: { guestPlaceholder: true },
    },
    {
      id: `shape:guest-back-image-${Date.now() + 1}` as TLShapeId,
      type: "customImage",
      props: { assetId: backAssetId, w: backW, h: backH },
      x: back.x,
      y: back.y,
      meta: { guestPlaceholder: true },
    },
  ]);

  try {
    window.localStorage.setItem(markerKey, "true");
  } catch {
    /* ignore */
  }

  return "injected";
};

/**
 * Removes any shapes marked as guest marketing placeholders. Called when
 * the guest opts into "+ New Design" — the placeholders are intro-only
 * artwork, they shouldn't sit underneath whatever the guest is about to
 * choose as a fresh format.
 */
export const removeGuestPlaceholders = (editor: any): void => {
  if (!editor) return;
  try {
    const shapes = editor.getCurrentPageShapes?.() || [];
    const ids = shapes.filter((s: any) => s?.meta?.guestPlaceholder === true).map((s: any) => s.id);
    if (ids.length > 0) editor.deleteShapes(ids);
  } catch {
    /* best-effort — placeholders aren't critical to clean up */
  }
};
